import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Card,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Divider,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import { useRecoilState } from "recoil";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import dayjs from "dayjs";
import { customFormat } from "../common/custom-format";
import { SAND_CAT } from "../config";
import LoadingSpinner from "../common/loading";
import theme from "../theme";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import { developerAccessTokenState } from "../global-state";

const AssistantHistoryList = () => {
  const [groupedHistories, setGroupedHistories] = useState({});
  const [data, setData] = useState([]);
  const [expandedHistories, setExpandedHistories] = useState([]);
  const currentRequestRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(dayjs());
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const observerRef = useRef(null);
  const hasMore = useRef(true);
  const nextToken = useRef("");
  const minSize = useRef(0);
  const [developerAccessToken] = useRecoilState(developerAccessTokenState);
  const selectedDate = useRef("");
  const lastFilterTimestampRef = useRef(null);
  const pastSevenDays = useRef([]);

  useEffect(() => {
    const today = new Date();
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date();
      date.setDate(today.getDate() - i);
      dates.push(date.toISOString().split("T")[0]);
    }
    pastSevenDays.current = dates;
  }, []);

  const handleDateChange = event => {
    setGroupedHistories({});
    setData([]);
    nextToken.current = "";
    fetchData(true);
  };

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: "0px",
      threshold: 1.0,
    };

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    const observer = new IntersectionObserver(entries => {
      const target = entries[0];
      if (target.isIntersecting && !loading && hasMore.current) {
        fetchData(false);
      }
    }, options);

    observerRef.current = observer;

    const observerTarget = document.querySelector(".observer-target");
    if (observerTarget) {
      observer.observe(observerTarget);
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentTime(dayjs());
    }, 60000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (!data || data.length === 0) return;
    const grouped = data.reduce((acc, History) => {
      const day = dayjs(History.timestamp).format("YYYY-MM-DD");
      if (!acc[day]) acc[day] = [];
      acc[day].push(History);
      return acc;
    }, {});
    setGroupedHistories(grouped);
    console.log(grouped);
  }, [data]);

  const fetchData = async (isReset = false) => {
    if (loading || (!hasMore.current && !isReset)) return;

    const filterTimestamp = selectedDate.current
      ? dayjs(selectedDate.current).endOf("day").valueOf()
      : null;

    if (
      filterTimestamp &&
      filterTimestamp === lastFilterTimestampRef.current &&
      isReset
    ) {
      console.log("Filter timestamp has not changed, skipping fetch.");
      return;
    }

    if (isReset) {
      minSize.current = 0;
      setGroupedHistories({});
      setData([]);
      nextToken.current = "";
      setHistoryLoading(true);
      hasMore.current = true;
    } else {
      setLoading(true);
    }

    const requestId = Symbol();
    currentRequestRef.current = requestId;

    try {
      const token = isReset ? "" : nextToken.current;
      const encodedToken = token ? encodeURIComponent(token) : null;

      const response = await fetch(
        `${SAND_CAT}/assistant/history/list?size=20&model=gemini-1.5-flash${
          encodedToken ? `&nextToken=${encodedToken}` : ""
        }${
          filterTimestamp === lastFilterTimestampRef.current
            ? ""
            : `&filterTimestamp=${filterTimestamp}`
        }`,
        {
          method: "GET",
          headers: {
            DeveloperAuthorization: `Bearer ${developerAccessToken}`,
            "Content-Type": "application/json",
          },
        },
      );
      const jsonData = await response.json();
      if (!response.ok) {
        throw new Error(jsonData);
      }

      if (currentRequestRef.current !== requestId) {
        console.log("return");
        return;
      }

      lastFilterTimestampRef.current = filterTimestamp;

      if (lastFilterTimestampRef.current !== filterTimestamp) {
        console.log("Filter timestamp has been updated, ignoring result.");
        return;
      }

      let histories = [];

      if (jsonData.assistantHistories) {
        histories = jsonData.assistantHistories;
      }

      nextToken.current = jsonData.page.nextToken;
      hasMore.current = jsonData.page.hasMore;

      const sortedHistories = (histories || []).sort(
        (a, b) => b.timestamp - a.timestamp,
      );

      setData(prev =>
        isReset ? sortedHistories : [...prev, ...sortedHistories],
      );

      if (jsonData.page.hasMore && minSize.current < 20) {
        minSize.current += histories.length;
        if (minSize.current < 20 && jsonData.page.hasMore) {
          await fetchData(false);
        }
      }
    } catch (error) {
      console.error("Failed to fetch automations:", error);
    } finally {
      if (currentRequestRef.current === requestId) {
        setLoading(false);
        setHistoryLoading(false);
      }
    }
  };

  const handleToggleHistory = index => {
    setExpandedHistories(prev =>
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index],
    );
  };

  return (
    <>
      <Box>
        <Card
          sx={{
            mb: 1,
            backgroundColor: "background.paper",
          }}
          elevation={0}
        >
          <Box
            sx={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              p: 2,
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                minWidth: "200px",
                maxWidth: "400px",
                height: "40px",
              }}
            >
              <Autocomplete
                options={pastSevenDays.current.map(date => ({ name: date }))}
                getOptionLabel={option => option.name}
                value={
                  pastSevenDays.current
                    .map(date => ({ name: date }))
                    .find(option => option.name === selectedDate.current) ||
                  null
                }
                onChange={(event, newValue) => {
                  if (newValue) {
                    selectedDate.current = newValue.name;
                    handleDateChange();
                  }
                }}
                disableClearable
                freeSolo={false}
                renderInput={params => (
                  <TextField
                    {...params}
                    placeholder="Select a date"
                    variant="outlined"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      sx: {
                        backgroundColor: "background.input",
                        borderRadius: "8px",
                      },
                    }}
                    sx={{
                      width: "100%",
                      height: "100%",
                      "& .MuiOutlinedInput-root": {
                        height: "100%",
                      },
                    }}
                  />
                )}
              />
            </Box>
          </Box>
        </Card>

        {historyLoading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              py: 4,
            }}
          >
            <CircularProgress size={24} />
          </Box>
        )}
        {!historyLoading && (
          <>
            {Object.keys(groupedHistories).length > 0 ? (
              Object.keys(groupedHistories).map((day, idx) => (
                <Box key={idx} sx={{ mb: 4 }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    {day}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  {groupedHistories[day].map((history, index) => (
                    <Accordion
                      key={index}
                      expanded={expandedHistories.includes(`${day}-${index}`)}
                      onChange={() => handleToggleHistory(`${day}-${index}`)}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            width: "100%",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItem: "center",

                              width: "100%",
                            }}
                          >
                            <Typography variant="body1" fontWeight="bold">
                              task
                            </Typography>
                          </Box>

                          <Typography variant="caption" color="textSecondary">
                            {customFormat(
                              dayjs(history.timestamp),
                              currentTime,
                            )}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        {history?.task?.draftScript && (
                          <React.Fragment>
                            <Divider />
                            <Box
                              sx={{
                                display: "flex",
                                mt: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  mt: 3,
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  backgroundColor: history?.task?.draftSuccess
                                    ? theme.palette.background.l6
                                    : "error.main",
                                  flexShrink: 0,
                                  marginRight: "8px",
                                }}
                              />
                              <Typography
                                key={index}
                                sx={{
                                  wordWrap: "break-word",
                                  whiteSpace: "pre-wrap",
                                }}
                                noWrap={false}
                                component="div"
                              >
                                <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                                  {history?.task?.draftScript}
                                </ReactMarkdown>
                              </Typography>
                            </Box>
                          </React.Fragment>
                        )}
                        {history?.task?.finalScript && (
                          <React.Fragment>
                            <Divider />
                            <Box
                              sx={{
                                display: "flex",
                                mt: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  mt: 3,
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  backgroundColor: history?.task?.finalSuccess
                                    ? theme.palette.background.l6
                                    : "error.main",
                                  flexShrink: 0,
                                  marginRight: "8px",
                                }}
                              />
                              <Typography
                                key={index}
                                sx={{
                                  wordWrap: "break-word",
                                  whiteSpace: "pre-wrap",
                                }}
                                noWrap={false}
                                component="div"
                              >
                                <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                                  {history?.task?.finalScript}
                                </ReactMarkdown>
                              </Typography>
                            </Box>
                          </React.Fragment>
                        )}
                        {history?.task?.instructions.map(
                          (instruction, index) => (
                            <React.Fragment key={index}>
                              <Divider />
                              <Typography
                                key={index}
                                sx={{
                                  mb: 1,
                                  mt: -1,
                                  wordWrap: "break-word",
                                  whiteSpace: "pre-wrap",
                                }}
                                noWrap={false}
                                component="div"
                              >
                                <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                                  {instruction}
                                </ReactMarkdown>
                              </Typography>
                            </React.Fragment>
                          ),
                        )}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              ))
            ) : (
              <Typography variant="body1" color="textSecondary">
                No histories available.
              </Typography>
            )}
          </>
        )}
        <Box
          className="observer-target"
          ref={el => {
            if (el && observerRef.current) {
              observerRef.current.observe(el);
            }
          }}
          sx={{
            height: "20px",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {loading && !historyLoading && <LoadingSpinner />}
        </Box>
        {!loading &&
          !hasMore.current &&
          Object.keys(groupedHistories).length > 0 && (
            <Typography
              variant="body2"
              sx={{ textAlign: "center", p: 2, color: "text.secondary" }}
            >
              No more activities
            </Typography>
          )}
      </Box>
    </>
  );
};

export default AssistantHistoryList;
